import { first, groupBy, omit, orderBy } from 'lodash';
import {
  CalibrationInfo,
  CalibrationItem,
  CalibrationItemWithDisplay,
  CalibrationOriginalItem,
  Question,
} from 'types';

import { isStrictlyObject } from 'utils/util/util';
import { toYesNoValue } from 'views/Widgets/Inputs/MultipleChoiceInput';

export const decoupleValAndCalibrationInfo = (
  value: any
): { val: any; calibrationInfo: CalibrationInfo } => {
  const calibrationInfo = extractCalibrationInfo(value);
  const val = normalizeValue(value);
  return { val, calibrationInfo };
};

const extractCalibrationInfo = (value: any) => {
  // Case Non array object
  if (isStrictlyObject(value)) {
    // check if one of the sub object has a property called calibrationInfo
    // i.e. {"mario":{"calibrationInfo" :{...}}, "john" : {"calibrationInfo" :{...}}}
    const calibrationInfoFromObj: CalibrationInfo | undefined = Object.values(
      value
    ).reduce((acc: CalibrationInfo | undefined, item) => {
      // @ts-expect-error
      if (!!item && !!item?.calibrationInfo) {
        if (acc) {
          return mergeCalibrationInfos(
            acc,
            // @ts-expect-error
            item?.calibrationInfo
          );
        }
        // @ts-expect-error
        return item.calibrationInfo;
      }
      return acc;
    }, undefined);

    if (calibrationInfoFromObj) {
      return calibrationInfoFromObj;
    }
    // Check in case the direct value has a property calibrationInfo
    return value?.calibrationInfo;
  }
  return undefined;
};

const normalizeValue = (value: any) => {
  // Case Non array object
  if (isStrictlyObject(value)) {
    // remove the item as a top level property if present
    if (Object.hasOwn(value, 'calibrationInfo')) {
      const valWithoutCal = omit(value, 'calibrationInfo');
      return valWithoutCal?.display ?? valWithoutCal;
    }

    // unstash the old value saved in the legacy property 'display'
    if (
      Object.values(value).find(
        (ob: any) => !!ob && Object.hasOwn(ob, 'display')
      )
    ) {
      return Object.fromEntries(
        Object.entries(value).map(([k, v]: [string, any]) => [k, v?.display])
      );
    }
  }
  return value;
};

export const groupBySurveyResponseId = (
  calibrationItems: CalibrationItem[]
): Record<number, CalibrationItem[]> => {
  return groupBy(
    orderBy(calibrationItems, 'created_at', 'desc'),
    'survey_response'
  );
};

export const NO_PRIORITY_ANSWER: number = 0;
export const IMPORTANT_ANSWER: number = 10;

export const mergeCalibrationInfos = (
  calibrationInfo1: CalibrationInfo | undefined,
  calibrationInfo2: CalibrationInfo | undefined
): CalibrationInfo | undefined => {
  if (!!calibrationInfo1 && !!calibrationInfo2) {
    const originalResponses: CalibrationOriginalItem[] = [
      ...(calibrationInfo2.originalResponses ?? []),
      ...(calibrationInfo1.originalResponses ?? []),
    ];

    const { currentValue, priorityCurrentValue } = originalResponses.reduce(
      (
        result: { currentValue: unknown; priorityCurrentValue: unknown },
        item: CalibrationOriginalItem
      ) => {
        if (item.priority === IMPORTANT_ANSWER) {
          return { currentValue: item.value, priorityCurrentValue: item.value };
        } else if (!result.currentValue) {
          return { currentValue: item.value, priorityCurrentValue: undefined };
        }
        return result;
      },
      { currentValue: undefined, priorityCurrentValue: undefined }
    );

    const calibratedValue = (
      (calibrationInfo1 && calibrationInfo1.calibrationItems) ??
      []
    ).length
      ? // @ts-expect-error
        first(calibrationInfo1.calibrationItems).value
      : undefined;

    return {
      ...calibrationInfo1,
      ...calibrationInfo2,
      currentValue: calibratedValue ?? currentValue,
      priorityCurrentValue: calibratedValue ?? priorityCurrentValue,
      originalResponses,
    };
  }

  return calibrationInfo1 ?? calibrationInfo2;
};

export const RECOMMEND_FOR_PROMOTION_QUESTION = (
  label,
  helperText
): Question => ({
  key: 'recommend_for_promotion',
  // @ts-expect-error
  label,
  type: 'YES_NO',
  helperText,
});

export const extractCalibrationInfoForNominatedForPromotion = ({
  manager,
  surveyResponseId,
  promotion_comments,
  recommend_for_promotion,
  recommend_for_promotion_additional_managers,
  surveyResponseIdToCalibrationItems,
}): CalibrationInfo => {
  const calibrationItems: CalibrationItemWithDisplay[] = (
    surveyResponseIdToCalibrationItems[surveyResponseId] || []
  )
    .filter((item) => item.key === 'recommend_for_promotion')
    .map((item) => ({ ...item, displayValue: '' }));

  return {
    calibrationItems: calibrationItems,
    currentValue:
      toYesNoValue(first(calibrationItems)?.value) ??
      toYesNoValue(recommend_for_promotion),
    priorityCurrentValue: undefined,
    originalResponses: [
      ...(toYesNoValue(recommend_for_promotion) != undefined
        ? [
            {
              author: manager || { id: 0, full_name: 'Unknown' },
              value: toYesNoValue(recommend_for_promotion),
              comment: promotion_comments,
              priority: 10,
            },
          ]
        : []),
      ...(recommend_for_promotion_additional_managers ?? []).map(
        ({
          manager: additional_manager,
          recommend_for_promotion: additional_manager_recommend_for_promotion,
          promotion_comments: additional_manager_promotion_comments,
        }) => ({
          author: additional_manager || { id: 0, full_name: 'Unknown' },
          value: toYesNoValue(additional_manager_recommend_for_promotion),
          comment: additional_manager_promotion_comments,
          priority: 0,
        })
      ),
    ],
    surveyResponseId,
  };
};
